import { cognitoConfig, oidcStorageKey } from '../auth/config.ts';

// Realtime transport config (PRD-0006 D10). The browser subscribes to fleet
// events over MQTT-over-WebSocket at the IoT data endpoint, authenticated by the
// custom authorizer Lambda with the Cognito ID token.
//
// Entirely env-driven, same discipline as auth/config.ts: when VITE_IOT_ENDPOINT
// (or the Cognito config, or the authorizer name) is absent — local dev against
// uvicorn, or the GCP/IAP deployment — realtime is DISABLED and callers fall
// back to polling. No IoT, no regression.

export interface RealtimeConfig {
  endpoint: string; // the iot:Data-ATS endpoint host (terraform output iot_data_endpoint)
  authorizerName: string; // the IoT custom authorizer (terraform output iot_browser_authorizer_name)
  // Reads the CURRENT Cognito ID token from storage. A getter (not a snapshot)
  // so every (re)connect presents a fresh token — the token expires ~60 min and
  // oidc-client-ts renews it in place; an mqtt.js client that captured the token
  // once would reconnect forever with an expired token (AUTHORIZATION_FAILURE).
  getToken: () => string | null;
}

// Read the current config, or null if realtime can't run. `getToken` is resolved
// lazily so a freshly-renewed token is used on every (re)connect.
export function realtimeConfig(): RealtimeConfig | null {
  const endpoint = import.meta.env.VITE_IOT_ENDPOINT as string | undefined;
  const authorizerName = import.meta.env.VITE_IOT_AUTHORIZER_NAME as string | undefined;
  if (!endpoint || !authorizerName) return null;

  const cfg = cognitoConfig();
  if (!cfg) return null; // no Cognito → no ID token to present → no realtime
  const key = oidcStorageKey(cfg);
  if (!idTokenFromStorage(key)) return null; // no token yet → polling

  return { endpoint, authorizerName, getToken: () => idTokenFromStorage(key) };
}

// The ID token is stored by oidc-client-ts in sessionStorage; read it outside
// React so plain-module transports don't need context (mirrors auth/apiFetch).
function idTokenFromStorage(key: string): string | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const user = JSON.parse(raw) as { id_token?: string };
    return user.id_token ?? null;
  } catch {
    return null;
  }
}
